import { GlobeIcon, LockIcon, UsersIcon, type LucideIcon } from 'lucide-react';

import type { AutomationVisibility } from '@/lib/api/automations.js';

export type { AutomationVisibility };

/** One level the automation audience dialog offers, in the order it lists them. */
export interface AutomationAudienceLevel {
  visibility: AutomationVisibility;
  label: string;
  hint: string;
  icon: 'lock' | 'users' | 'globe';
}

/**
 * The levels a scheduled automation can be moved between, widest last. Same hints as
 * `DASHBOARD_AUDIENCE_LEVELS` — a user who learned one audience picker does not relearn it here.
 */
export const AUTOMATION_AUDIENCE_LEVELS: AutomationAudienceLevel[] = [
  {
    visibility: 'private',
    label: 'Private',
    hint: 'Only you',
    icon: 'lock',
  },
  {
    visibility: 'group',
    label: 'Group',
    hint: 'You and the members you pick',
    icon: 'users',
  },
  {
    visibility: 'workspace',
    label: 'Workspace',
    hint: 'Everyone in the workspace',
    icon: 'globe',
  },
];

const AUTOMATION_AUDIENCE_ICONS: Record<
  AutomationAudienceLevel['icon'],
  LucideIcon
> = { lock: LockIcon, users: UsersIcon, globe: GlobeIcon };

/**
 * The icon for a visibility level — one map shared by the audience badge and any other place that
 * shows "who sees this" at a glance, so the mapping is defined once.
 */
export function automationVisibilityIcon(
  visibility: AutomationVisibility,
): LucideIcon {
  const level =
    AUTOMATION_AUDIENCE_LEVELS.find((item) => item.visibility === visibility) ??
    AUTOMATION_AUDIENCE_LEVELS[0]!;
  return AUTOMATION_AUDIENCE_ICONS[level.icon];
}
